import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
// dialog removed: verification checkbox is shown inline now
import PrimaryButton from '../components/PrimaryButton';
import InfoModal from '../components/InfoModal';

function EligibilityCheck() {
  const [school, setSchool] = useState('');
  const [schoolId, setSchoolId] = useState(null);
  const [affiliation, setAffiliation] = useState('');
  const [enrollment, setEnrollment] = useState('');
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [agree, setAgree] = useState(false);
  const [touched, setTouched] = useState({ school: false, affiliation: false, agree: false, enrollment: false });
  const [serverError, setServerError] = useState('');
  const [schoolLockMessage, setSchoolLockMessage] = useState('');
  // addConfirmOpen removed; verification shown inline
  const [addVerifyChecked, setAddVerifyChecked] = useState(false);
  const [pendingAddName, setPendingAddName] = useState('');
  const [addError, setAddError] = useState('');
  const [isAddedSelection, setIsAddedSelection] = useState(false);
  const [addVerified, setAddVerified] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoQuestion, setInfoQuestion] = useState('');

  const fetchSchools = async (q) => {
    try {
      const res = await axios.get(`/api/public/eligibility/schools?name=${encodeURIComponent(q)}`);
      setSchoolOptions(res.data || []);
      setServerError('');
    } catch (err) {
      setSchoolOptions([]);
      setServerError('Failed to fetch schools. Please try again.');
    }
  };

  const handleInputChange = (event, value, reason) => {
    // keep the visible input in sync
    setInputValue(value || '');
    if (reason === 'input') {
      setSchool(value);
      setPendingAddName(value || '');
      if (value && value.length > 1) {
        fetchSchools(value);
      }
    }
  };

  // Show the "add new school" verification as the user types when there's no matching option.
  useEffect(() => {
    const trimmed = (inputValue || '').toString().trim();
    if (trimmed && trimmed.length > 1) {
      const exists = schoolOptions.some(s => s.name && s.name.toLowerCase() === trimmed.toLowerCase());
      setPendingAddName(trimmed);
      if (!exists) {
        setIsAddedSelection(true);
        setAddVerified(false);
      } else {
        // matching school exists: ensure we treat it as existing selection
        setIsAddedSelection(false);
        setAddVerified(true);
      }
    } else {
      setIsAddedSelection(false);
      setAddVerified(false);
    }
  }, [inputValue, schoolOptions]);

  const handleSchoolSelect = (event, value) => {
    setSchoolLockMessage('');
    setAddError('');
    if (value && typeof value === 'object') {
      // user selected the special 'add' option
      if (value.isAdd) {
        const raw = value.rawName || '';
        setPendingAddName(raw);
        // do NOT create on select; defer creation until submit after verification
        setSchool(raw);
        setInputValue(raw);
        setSchoolId(null);
        setIsAddedSelection(true);
        setAddVerified(false);
        return;
      }
      if (value.applicationInProgress) {
        setSchoolLockMessage("It looks like your school already has an application in progress, please contact your school administrator. If you believe this to be an error, please contact us.");
        setSchool('');
        setSchoolId(null);
        setIsAddedSelection(false);
        setAddVerified(false);
        return;
      }
      setSchool(value.name);
      setInputValue(value.name);
      setSchoolId(value.id);
      setIsAddedSelection(false);
      setAddVerified(true);
    } else if (typeof value === 'string') {
      // freeSolo typed string selected
      setSchool(value);
      setInputValue(value);
      setSchoolId(null);
      setIsAddedSelection(false);
      setAddVerified(false);
    } else {
      setSchool('');
      setSchoolId(null);
      setIsAddedSelection(false);
      setAddVerified(false);
    }
  };

  // removed handleConfirmAdd: verification is inline and handled by checkbox

  const handleBlur = (field) => {
    setTouched((t) => ({ ...t, [field]: true }));
    if (field === 'school') {
      // If the user typed a school name that doesn't exist in the fetched options,
      // show the 'add school' verification so they cannot bypass it by tabbing away.
      const trimmed = (school || '').toString().trim();
      const exists = trimmed && schoolOptions.some(s => s.name && s.name.toLowerCase() === trimmed.toLowerCase());
      if (trimmed && !exists) {
        setPendingAddName(trimmed);
        setIsAddedSelection(true);
        // require explicit verification
        setAddVerified(false);
      }
    }
  };

  const validate = () => {
    return {
      school: Boolean(school && school.toString().trim().length > 0),
      schoolId: isAddedSelection ? true : Boolean(schoolId),
      affiliation: Boolean(affiliation),
      enrollment: Boolean(enrollment),
      agree: Boolean(agree),
      addVerified: isAddedSelection ? Boolean(addVerified) : true,
    };
  };

  const errors = validate();
  const formValid = Object.values(errors).every(Boolean);
  const handleContinue = () => {
    if (formValid) {
      navigate('/signup', { state: { schoolId, schoolName: school, enrollment } });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({ school: true, affiliation: true, agree: true, enrollment: true });
    if (!formValid) return;

    // If user added a new school, require verification and only create if affiliation is School Administrator
    const isAdmin = affiliation === 'School Administrator and/or Faculty';
    if (isAddedSelection) {
      if (!addVerified) {
        // should not happen due to validate, but guard anyway
        return;
      }
      if (!isAdmin || enrollment !== 'Under 16,000 Students') {
        // do not create school for non-admins or for disallowed enrollment
        navigate('/notqualified');
        return;
      }

      // create school now, then continue to signup
      (async () => {
        try {
          const res = await axios.post('/api/public/eligibility/add-school', { name: pendingAddName || school });
          const created = res?.data;
          if (created) {
            // ensure it's in local options
            setSchoolOptions((prev) => {
              if (prev.some(s => s.name && s.name.toLowerCase() === (created.name || '').toLowerCase())) return prev;
              return [...prev, { id: created.id || null, name: created.name || (pendingAddName || school), applicationInProgress: false }];
            });
            if (created.id) setSchoolId(created.id);
            setSchool(created.name || pendingAddName || school);
            setInputValue(created.name || pendingAddName || school);
            navigate('/signup', { state: { schoolId: created.id || null, schoolName: created.name || pendingAddName || school, enrollment } });
          } else {
            // fallback: proceed without id if backend didn't return one
            navigate('/signup', { state: { schoolId: null, schoolName: created?.name || school, enrollment } });
          }
        } catch (err) {
          setServerError(err?.response?.data?.message || 'Unable to add school. Please try again later.');
        }
      })();
      return;
    }

    // existing school path
    // existing school path: require both admin affiliation and Under 16,000 Students
    if (isAdmin && enrollment === 'Under 16,000 Students') {
      handleContinue();
    } else {
      navigate('/notqualified');
    }
  };

  const affiliationOptions = useMemo(
    () => [
      'School Administrator and/or Faculty',
      'Current Student',
      'Alumni',
      'General School Supporter',
      'Other',
    ],
    []
  );

  const enrollmentOptions = useMemo(() => ['Under 16,000 Students', 'Above 16,000 Students'], []);

  return (
    <Box component="main">
      <Grid container justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', px: 3, py: { xs: 1.5, md: 2 } }}>
              <Box
                component="img"
                src="ryslogo.png"
                alt="Retool Your School"
                sx={{ height: { xs: 42, md: 60 }, objectFit: 'contain' }}
              />
            </Box>

            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                Welcome to The Home Depot 2026 Retool Your School Grant Application
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Welcome to The Home Depot Retool Your School Grant Application Portal! Explore various grants tailored to support your educational pursuits. Whatever your campus projects entail, we have a grant for you. If you have any questions, please refer to the{' '}
                <Typography component="span" sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }} onClick={(e) => { e.preventDefault(); setInfoQuestion(''); setInfoOpen(true); }}>
                  FAQ
                </Typography>
                .
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>School Information</Typography>

                {(() => {
                  const trimmed = (school || '').trim();
                  const displayOptions = schoolOptions.slice();
                  const exists = trimmed && schoolOptions.some(s => s.name && s.name.toLowerCase() === trimmed.toLowerCase());
                  if (trimmed && trimmed.length > 1 && !exists) {
                    displayOptions.push({ id: '__add', name: `Add "${trimmed}"`, isAdd: true, rawName: trimmed });
                  }

                  return (
                    <>
                      <Autocomplete
                        freeSolo
                        options={displayOptions}
                        getOptionLabel={(option) => (typeof option === 'string' ? option : (option.name || ''))}
                        value={schoolOptions.find(s => s.id === schoolId) || null}
                        inputValue={inputValue}
                        onInputChange={handleInputChange}
                        onChange={handleSchoolSelect}
                        isOptionDisabled={(option) => option && option.applicationInProgress}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Begin Typing Your School Name"
                            variant="outlined"
                            sx={{ mb: 2 }}
                            error={touched.school && !errors.school}
                            helperText={schoolLockMessage || (touched.school && !errors.school ? 'Please enter your school name' : '')}
                            onBlur={() => handleBlur('school')}
                            InputProps={{
                              ...params.InputProps,
                            }}
                          />
                        )}
                      />

                      {isAddedSelection && (
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ mb: 1 }} variant="body2">By adding your school, you are verifying that you are faculty or staff of the school.</Typography>
                          <FormControlLabel
                            control={<Checkbox checked={addVerifyChecked} onChange={(e) => { setAddVerifyChecked(e.target.checked); setAddVerified(e.target.checked); }} />}
                            label="I verify that I am faculty or staff of the school to be added."
                          />
                          {addError && (
                            <Box sx={{ mt: 2 }}>
                              <Typography color="error" variant="body2">{addError}</Typography>
                            </Box>
                          )}
                          {!addVerified && (
                            <Typography sx={{ color: 'warning.main', mt: 1 }} variant="body2">You must verify this association before continuing.</Typography>
                          )}
                        </Box>
                      )}
                    </>
                  );
                })()}

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="enrollment-label">School Enrollment</InputLabel>
                  <Select
                    labelId="enrollment-label"
                    label="School Enrollment"
                    value={enrollment}
                    onChange={(e) => setEnrollment(e.target.value)}
                    onBlur={() => handleBlur('enrollment')}
                    error={touched.enrollment && !errors.enrollment}
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {enrollmentOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="affiliation-label">School Affiliation</InputLabel>
                  <Select
                    labelId="affiliation-label"
                    label="School Affiliation"
                    value={affiliation}
                    onChange={(e) => setAffiliation(e.target.value)}
                    onBlur={() => handleBlur('affiliation')}
                    error={touched.affiliation && !errors.affiliation}
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {affiliationOptions.map((a) => (
                      <MenuItem key={a} value={a}>{a}</MenuItem>
                    ))}
                  </Select>
                </FormControl>



                {serverError ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography color="error" variant="body2">{serverError}</Typography>
                  </Box>
                ) : null}

                <FormControlLabel
                  control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} onBlur={() => handleBlur('agree')} />}
                  label={
                    <span>
                      Agree to The{' '}
                      <Typography component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', color: 'primary.main', fontWeight: 700 }} onClick={(e) => { e.stopPropagation(); e.preventDefault(); setInfoQuestion('What are the specific eligibility requirements?'); setInfoOpen(true); }}>
                        Eligibility Criteria
                      </Typography>
                    </span>
                  }
                />
                <InfoModal open={infoOpen} onClose={() => setInfoOpen(false)} openQuestion={infoQuestion} />
                <Box sx={{ flex: 1 }} />
                <PrimaryButton type="submit" disabled={!formValid}>Check Eligibility</PrimaryButton>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', pt: '50px' }}>
          <PrimaryButton onClick={() => navigate('/login')} sx={{ bgcolor: 'primary.main', color: '#fff', textTransform: 'none', fontSize: '1.05rem' }}>
            Already Started Your Application? Click Here to Login
          </PrimaryButton>
        </Grid>
      </Grid>
    </Box>
  );
}

export default EligibilityCheck;
